import { IS_PROD_BUILD } from '@src/config/env'
import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getQueryParam } from '@src/quince-site-common/src/utils/get-query-param'

/**
 * Dev/QA-only escape hatch. The standard `?swipeable-product-card-clp=show` override only injects a
 * forced bucket into `statsigUser.custom`; Statsig still computes the real bucket from the dashboard,
 * so while the experiment is not yet started it resolves to `control` and the param is inert.
 * To let the carousel be tested before the experiment is started, honor the query param directly in
 * any non-production context. Never active in a production build (`NODE_ENV === 'production'` AND a
 * prod build env).
 */
function isCarouselForcedShowInNonProd(): boolean {
  if (process.env.NODE_ENV === 'production' && IS_PROD_BUILD) return false
  return getQueryParam(STATSIG_EXPERIMENTS.SWIPEABLE_PRODUCT_CARD_CLP) === STATSIG_EXPERIMENTS_BUCKETS.SHOW
}

/**
 * Gates the in-grid swipeable image carousel on the product card (CLP + Search, mobile web only).
 *
 * `control` (default) renders the existing single-image card untouched; the `show` bucket renders
 * the carousel. We require `init` so the value is only trusted once Statsig has bootstrapped — with
 * SSR bootstrap this is true on first client paint, so there is no flash and no layout shift for
 * non-exposed users. Read once per CLP/Search surface and threaded down as `enableImageCarousel`.
 *
 * Exposure is only logged for mobile users — desktop users can never see the carousel, so bucketing
 * them would dilute the experiment readout.
 */
export function useSwipeableProductCardExperiment(): boolean {
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const isForced = isCarouselForcedShowInNonProd()
  const { experimentBucket, init } = useStatsigExperiment(
    STATSIG_EXPERIMENTS.SWIPEABLE_PRODUCT_CARD_CLP,
    isMobile && !isForced,
  )
  if (isForced) return true
  return init && experimentBucket === STATSIG_EXPERIMENTS_BUCKETS.SHOW
}
